import React, { useState, useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import { Tooltip, Typography, InputLabel, FormControl, FormHelperText } from '@mui/material';
import {
    DatePicker,
    StepperControls,
    Select,
    StepWrapper,
    StyledCardSection,
    TextField,
} from '@fnol/library/src/components';
import {
    common,
    getIsPortal,
    getIsWebsite,
    getIsCallCenter,
    getApplicationId,
    prepareHours,
} from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks/useApi';
import { checkPolicyNumber, checkVIN, prepareActivePoliciesForState, prepareMinutes } from '../../lib/helpers';
import { translations, question30MaxLength } from '../../lib/consts';
import { getActivePolicies, getPolicyDetails, getUserData } from '../../rpc/actions';
import { ActivePoliciesSelect } from './ActivePoliciesSelect';
import './policy.css';

type GenericObject = { [key: string]: any };

type PolicyDetailScreenProps = {
    navigator: GenericObject[];
    values: GenericObject[];
};

const PolicyDetailScreen = ({ navigator }: PolicyDetailScreenProps) => {
    const [currentStep, { goForward }] = navigator;
    const { values, setFieldValue, setErrors, setTouched, errors, touched } = useFormikContext<GenericObject>();

    const toDate = (date: string) => {
        return new Date(date);
    };

    const data = {
        BrojPolise: values.policyNumber,
        BrojSasije: values.chassisNumber,
        DatumNastanka:
            values.dateOfOccurance !== ''
                ? `${toDate(values.dateOfOccurance).getFullYear()}-${(
                      '0' +
                      (toDate(values.dateOfOccurance).getMonth() + 1)
                  ).slice(-2)}-${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}`
                : values.dateOfOccurance,
        VremeNastanka: `${values.hourOfOccurance}:${values.minuteOfOccurance}`,
    };

    const saveVehicle = (res: GenericObject) => {
        if (res.data.poruka === '') {
            setFieldValue('vehicle', {
                vehicleBrand: res.data.markaVozila,
                vehicleType: res.data.tipVozila,
                vehicleReg: res.data.regOznaka,
            });
            setFieldValue('insureIsPerson', res.data.ugovaracFL);
            setFieldValue('policyNumber', res.data.brojPolise);
            setFieldValue('chassisNumber', res.data.brojSasije);
            setFieldValue('vehicleTypeID', res.data.vrstaVozilaId);
            setFieldValue('reportedClaims', res.data.prijavljeneStete);
            goForward();
        }
    };

    const preparedHours = prepareHours();
    const preparedMinutes = prepareMinutes();

    const { response: activePolicies, fetchMethod: fetchActivePolicies } = useApi(getActivePolicies);
    const { response: policyDetails, fetchMethod: fetchPolicyDetails } = useApi(
        () => getPolicyDetails(data),
        saveVehicle,
    );

    const handleNext = () => {
        let valid = true;
        setTouched(
            {
                policyOrChassisNumber: true,
                policyNumber: true,
                dateOfOccurance: true,
                hourOfOccurance: true,
                minuteOfOccurance: true,
                activePolicyId: true,
            },
            false,
        );
        const existingErrors: GenericObject = {};
        if (!getIsPortal(getApplicationId())) {
            const escapedPolicyOrChassisNumber = values.policyOrChassisNumber.replace(/\s/g, '');
            if (escapedPolicyOrChassisNumber === '') {
                existingErrors.policyOrChassisNumber =
                    translations.validationMessages.POLICY_OR_CHASSIS_NUMBER_REQUIRED;
                valid = false;
            } else if (!checkPolicyNumber(escapedPolicyOrChassisNumber) && !checkVIN(escapedPolicyOrChassisNumber)) {
                existingErrors.policyOrChassisNumber =
                    translations.validationMessages.POLICY_OR_CHASSIS_NUMBER_INVALID_FORMAT;
                valid = false;
            }
        }
        if (getIsPortal(getApplicationId()) && values.activePolicyId === '') {
            existingErrors.activePolicyId = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.dateOfOccurance === '' || values.dateOfOccurance === null) {
            existingErrors.dateOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.hourOfOccurance === '') {
            existingErrors.hourOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (values.minuteOfOccurance === '') {
            existingErrors.minuteOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);

        if (valid) {
            if (!getIsPortal(getApplicationId())) {
                const escapedPolicyOrChassisNumber = values.policyOrChassisNumber.replace(/\s/g, '');
                if (escapedPolicyOrChassisNumber !== '') {
                    if (checkPolicyNumber(escapedPolicyOrChassisNumber)) {
                        setFieldValue('policyNumber', escapedPolicyOrChassisNumber);
                        setFieldValue('chassisNumber', '');
                        data.BrojPolise = escapedPolicyOrChassisNumber;
                        data.BrojSasije = '';
                    } else if (checkVIN(escapedPolicyOrChassisNumber)) {
                        setFieldValue('policyNumber', '');
                        setFieldValue('chassisNumber', escapedPolicyOrChassisNumber);
                        data.BrojPolise = '';
                        data.BrojSasije = escapedPolicyOrChassisNumber;
                    }
                }
            }
            if (getIsPortal(getApplicationId())) {
                const escapedPolicyNumber = values.policyNumber.replace(/\s/g, '');
                setFieldValue('policyNumber', escapedPolicyNumber);
                data.BrojPolise = escapedPolicyNumber;
            }
            fetchPolicyDetails();
        }
    };

    const [preparedActivePolicies, setPreparedActivePolicies] = useState<any>([]);

    useEffect(() => {
        if (getIsPortal(getApplicationId())) {
            fetchActivePolicies();
            getUserData().then((response) => {
                response.data.ime && setFieldValue('personalName', response.data.ime);
                response.data.prezime && setFieldValue('personalLastName', response.data.prezime);
                response.data.jmbg && setFieldValue('personalUCN', response.data.jmbg);
                response.data.email && setFieldValue('personalEmail', response.data.email);
                if (response.data.telefon) {
                    setFieldValue('personalMobile', response.data.telefon);
                    setFieldValue('smsContactMobile', response.data.telefon);
                }
            });
        }
        setFieldValue('vehicleTypeID', policyDetails.data.vrstaVozilaId);
    }, []);

    useEffect(() => {
        if (Array.isArray(activePolicies?.data?.data)) {
            const activePoliciesArray = prepareActivePoliciesForState(activePolicies.data.data);
            setFieldValue('activePolicies', activePoliciesArray);
            setPreparedActivePolicies(activePoliciesArray);
        }
    }, [activePolicies]);

    const activePolicyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const activePolicy = values.activePolicies.filter((policy: GenericObject) => {
            return policy.id === e.target.value;
        });
        setFieldValue('activePolicyId', e.target.value);
        setFieldValue('policyNumber', activePolicy[0].policyNumber);
        setFieldValue('chassisNumber', activePolicy[0].chassisNumber);
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.VEHICLE}
                </Typography>
                {getIsWebsite(getApplicationId()) || getIsCallCenter(getApplicationId()) ? (
                    <>
                        <Tooltip
                            title={
                                <span>
                                    <p>{translations.POLICY_OR_CHASSIS_NUMBER_TOOLTIP_1}</p>
                                    <p>{translations.POLICY_OR_CHASSIS_NUMBER_TOOLTIP_2}</p>
                                </span>
                            }
                            placement="top-start"
                        >
                            <FormControl>
                                <Field
                                    name="policyOrChassisNumber"
                                    label={translations.POLICY_OR_CHASSIS_NUMBER}
                                    as={TextField}
                                    inputProps={{
                                        maxLength: question30MaxLength,
                                    }}
                                    error={touched.policyOrChassisNumber && errors.policyOrChassisNumber}
                                />
                                {touched.policyOrChassisNumber && errors.policyOrChassisNumber ? (
                                    <FormHelperText error={true}>
                                        <>{errors.policyOrChassisNumber}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        </Tooltip>
                    </>
                ) : null}
                {getIsPortal(getApplicationId()) ? (
                    <>
                        <FormControl error={!!(touched.activePolicyId && errors.activePolicyId)}>
                            <InputLabel id="activePolicyId">{translations.POLICY}</InputLabel>
                            <Field
                                name="activePolicyId"
                                label={translations.POLICY}
                                value={values.activePolicyId}
                                items={preparedActivePolicies}
                                onChange={activePolicyChange}
                                as={ActivePoliciesSelect}
                            />
                            {touched.activePolicyId && errors.activePolicyId ? (
                                <FormHelperText error={true}>
                                    <>{errors.activePolicyId}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                ) : null}
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.TIME_OF_OCCURANCE}
                </Typography>

                <FormControl error={!!(touched.dateOfOccurance && errors.dateOfOccurance)} className="policyDatePicker">
                    <Field
                        name="dateOfOccurance"
                        label={translations.DATE}
                        as={DatePicker}
                        disableFuture
                        error={!!(touched.dateOfOccurance && errors.dateOfOccurance)}
                    />
                    {touched.dateOfOccurance && errors.dateOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.dateOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                <FormControl error={!!(touched.hourOfOccurance && errors.hourOfOccurance)}>
                    <InputLabel id="hourOfOccurance">{translations.HOUR}</InputLabel>
                    <Field name="hourOfOccurance" label={translations.HOUR} items={preparedHours || []} as={Select} />
                    {touched.hourOfOccurance && errors.hourOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.hourOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
                <FormControl error={!!(touched.minuteOfOccurance && errors.minuteOfOccurance)}>
                    <InputLabel id="minuteOfOccurance">{translations.MINUTE}</InputLabel>
                    <Field
                        name="minuteOfOccurance"
                        label={translations.MINUTE}
                        items={preparedMinutes || []}
                        as={Select}
                    />
                    {touched.minuteOfOccurance && errors.minuteOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.minuteOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            {policyDetails.isSuccess && policyDetails?.data?.data.poruka ? (
                <FormHelperText error={true}>
                    <>{policyDetails?.data?.data.poruka}</>
                </FormHelperText>
            ) : (
                <></>
            )}
            <StepperControls onNext={handleNext} onNextLabel={common.NEXT} onPreviousLabel={common.PREVIOUS} />
        </StepWrapper>
    );
};

export default PolicyDetailScreen;
